import React, { useEffect, useRef, useState, useCallback } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiCheckbox,
  EuiLoadingSpinner,
  EuiSelect,
  EuiButtonIcon,
} from "@elastic/eui";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { AlertType } from "../../profileTypes";
import { useNewProfileAlertsListStyles } from "../../profileStyles";
import AlertObjectSelector from "./AlertObjectSelector";
import IconPopover from "components/Popover/IconPopover";
import FdDataTableRowActions from "components/DataTable/FdDataTableRowActions";
import { AlertTypeDetailsFlyout } from "pages/AdminConsole/Monitoring/AlertTypes/AlertTypeDetailsFlyout";
import { isNullOrUndefined } from "utils";
import BadgeCount from "components/Badge/BadgeCount";
import Badge from "components/Badge/Badge";
import { badgeColors } from "Consts";
import IconButton from "components/Buttons/IconButton";

interface Props {
  selectedAlerts: AlertType[];
  setSelectedAlerts: (alerts: AlertType[]) => void;
  setIsDirty: (isDirty: boolean) => void;
  availableAlertTypes: AlertType[];
  isSystemProfile?: boolean;
  monitoringProfileId: number;
}

interface ExtendedAlertType extends AlertType {
  isExpanded?: boolean;
  childRows?: AlertType[];
}

const getItemId = (alert: AlertType) =>
  `${alert.id}-${
    alert.subtypeId !== undefined && alert.subtypeId !== null ? alert.subtypeId.toString() : "null"
  }-${alert.alertLevel || 1}`;

const getSortValueInternal = (
  alert: AlertType,
  field: keyof AlertType,
  selectedAlerts: AlertType[]
) => {
  switch (field) {
    case "alertLevel":
      const sameGroupAlerts = selectedAlerts.filter(
        (a) => a.id === alert.id && a.subtypeId === alert.subtypeId
      );
      return sameGroupAlerts.length;
    default:
      return alert[field];
  }
};

const ProfileAlertsListAdvanced = ({
  selectedAlerts,
  setSelectedAlerts,
  setIsDirty,
  availableAlertTypes,
  isSystemProfile,
  monitoringProfileId,
}: Props) => {
  const { clientId } = useSelector(globalFilterSelector, shallowEqual);
  const classes = useNewProfileAlertsListStyles();
  const [sortField, setSortField] = useState<keyof AlertType>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const tableKey = useRef(0);
  const [selectedItem, setSelectedItem] = useState<AlertType | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [hasInitializedExpansion, setHasInitializedExpansion] = useState(false);

  // Expand all groups by default - only on initial load
  useEffect(() => {
    if (!hasInitializedExpansion && selectedAlerts.length > 0) {
      const allGroupKeys = new Set<string>();
      selectedAlerts.forEach((alert) => {
        const groupKey = `${alert.id}-${alert.subtypeId ?? "null"}`;
        allGroupKeys.add(groupKey);
      });
      setExpandedRows(allGroupKeys);
      setHasInitializedExpansion(true);
    }
  }, [selectedAlerts, hasInitializedExpansion]);

  const getSortValue = useCallback(
    (alert: AlertType, field: keyof AlertType) => {
      return getSortValueInternal(alert, field, selectedAlerts);
    },
    [selectedAlerts]
  );

  const onClose = () => {
    setSelectedItem(null);
  };

  const toggleRowExpansion = (alertId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const createExpandedItems = (): ExtendedAlertType[] => {
    const items: ExtendedAlertType[] = [];

    const groupedAlerts = selectedAlerts.reduce(
      (acc, alert) => {
        const groupKey = `${alert.id}-${alert.subtypeId ?? "null"}`;
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(alert);
        return acc;
      },
      {} as Record<string, AlertType[]>
    );

    Object.entries(groupedAlerts).forEach(([groupKey, alertsInGroup]) => {
      const isExpanded = expandedRows.has(groupKey);

      // Find level 1 alert to use as parent
      const level1Alert = alertsInGroup.find((alert) => (alert.alertLevel || 1) === 1);
      const parentAlert = level1Alert || alertsInGroup[0];

      // Get child alerts (levels 2 and 3 only)
      const childAlerts = alertsInGroup.filter((alert) => (alert.alertLevel || 1) > 1);

      items.push({
        ...parentAlert,
        isExpanded,
        childRows: alertsInGroup, // Keep all for reference
      });

      // Only show child rows for levels > 1
      if (isExpanded) {
        childAlerts.forEach((alert) => {
          items.push({
            ...alert,
            isChildRow: true,
          } as ExtendedAlertType & { isChildRow: boolean });
        });
      }
    });

    return items;
  };

  useEffect(() => {
    tableKey.current += 1;
  }, [selectedAlerts, expandedRows]);

  useEffect(() => {
    if (selectedAlerts && selectedAlerts.length > 0) {
      const sorted = [...selectedAlerts].sort((a, b) => {
        const aValue = getSortValue(a, sortField);
        const bValue = getSortValue(b, sortField);
        const modifier = sortDirection === "asc" ? 1 : -1;

        if (aValue === bValue) return 0;
        if (aValue === undefined || aValue === null) return sortDirection === "asc" ? -1 : 1;
        if (bValue === undefined || bValue === null) return sortDirection === "asc" ? 1 : -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return aValue.localeCompare(bValue) * modifier;
        }
        // @ts-ignore
        return (aValue < bValue ? -1 : 1) * modifier;
      });

      let orderChanged = selectedAlerts.length !== sorted.length;
      if (!orderChanged) {
        for (let i = 0; i < selectedAlerts.length; i++) {
          if (getItemId(selectedAlerts[i]) !== getItemId(sorted[i])) {
            orderChanged = true;
            break;
          }
        }
      }

      if (orderChanged) {
        setSelectedAlerts(sorted);
      }
    }
  }, [selectedAlerts, sortField, sortDirection, setSelectedAlerts, getSortValue]);

  const handleChangeSeverity = (
    e: React.ChangeEvent<HTMLSelectElement>,
    alertId: number,
    subtypeId: number | null,
    alertLevel: number
  ) => {
    if (isSystemProfile) return;

    const newSeverity = parseInt(e.target.value) as 1 | 3 | 5;

    const updatedAlerts = selectedAlerts.map((alert) => {
      if (
        alert.id === alertId &&
        alert.subtypeId === subtypeId &&
        alert.alertLevel === alertLevel
      ) {
        return {
          ...alert,
          severityTypeId: newSeverity,
        } as AlertType;
      }
      return alert;
    });

    setSelectedAlerts(updatedAlerts);
  };

  const handleChangeTolerance = (
    e: React.ChangeEvent<HTMLSelectElement>,
    alertId: number,
    subtypeId: number | null,
    alertLevel: number
  ) => {
    if (isSystemProfile) return;

    const newThreshold = parseInt(e.target.value) as 1 | 2 | 3;

    const updatedAlerts = selectedAlerts.map((alert) => {
      if (
        alert.id === alertId &&
        alert.subtypeId === subtypeId &&
        alert.alertLevel === alertLevel
      ) {
        return {
          ...alert,
          thresholdId: newThreshold,
        };
      }
      return { ...alert };
    });

    setSelectedAlerts(updatedAlerts);
    setIsDirty(true);
  };

  const processFormula = (alertType: AlertType): string => {
    const matchingAvailableAlert = availableAlertTypes.find(
      (available) =>
        available.id === alertType.id &&
        (available.subtypeId === alertType.subtypeId ||
          (!available.subtypeId && !alertType.subtypeId))
    );

    if (matchingAvailableAlert === undefined || matchingAvailableAlert.formula == null) {
      return "No Formula Defined";
    }

    if (!matchingAvailableAlert.thresholds?.length || !alertType.thresholdId) {
      return matchingAvailableAlert.formula;
    }

    const selectedThresholdDefinition = matchingAvailableAlert.thresholds.find(
      (th) => th.id === alertType.thresholdId
    );

    if (!selectedThresholdDefinition) {
      return matchingAvailableAlert.formula;
    }

    let processedFormula = matchingAvailableAlert.formula;

    selectedThresholdDefinition.values.forEach(({ key, value }) => {
      const placeholder = `$${key}`;
      processedFormula = processedFormula.replace(new RegExp("\\" + placeholder, "g"), value);
    });

    return processedFormula;
  };

  const getToleranceOptions = (alert: AlertType) => {
    const matchingAvailableAlert = availableAlertTypes.find(
      (available) =>
        available.id === alert.id &&
        (available.subtypeId === alert.subtypeId || (!available.subtypeId && !alert.subtypeId))
    );

    if (!matchingAvailableAlert?.thresholds) {
      return [];
    }

    const sameAlertTypeItems = selectedAlerts.filter(
      (a) => a.id === alert.id && a.subtypeId === alert.subtypeId
    );
    let highestRankFromLowerLevels = 0;
    let lowestRankFromHigherLevels = Infinity;

    sameAlertTypeItems.forEach((item) => {
      // Check items with lower alert levels
      if ((item.alertLevel || 1) < (alert.alertLevel || 1)) {
        const itemThreshold = matchingAvailableAlert.thresholds?.find(
          (th) => th.id === item.thresholdId
        );

        if (itemThreshold && itemThreshold.rank > highestRankFromLowerLevels) {
          highestRankFromLowerLevels = itemThreshold.rank;
        }
      }

      // Check items with higher alert levels
      if ((item.alertLevel || 1) > (alert.alertLevel || 1)) {
        // Only consider if this item has a selected threshold
        if (item.thresholdId) {
          const itemThreshold = matchingAvailableAlert.thresholds?.find(
            (th) => th.id === item.thresholdId
          );

          if (itemThreshold && itemThreshold.rank < lowestRankFromHigherLevels) {
            lowestRankFromHigherLevels = itemThreshold.rank;
          }
        }
      }
    });

    // Filter thresholds based on both conditions
    let availableThresholds = matchingAvailableAlert.thresholds;

    // Must have rank higher than highest rank from lower levels
    if (highestRankFromLowerLevels > 0) {
      availableThresholds = availableThresholds.filter(
        (threshold) => threshold.rank > highestRankFromLowerLevels
      );
    }

    // Must have rank lower than lowest rank from higher levels
    if (lowestRankFromHigherLevels < Infinity) {
      availableThresholds = availableThresholds.filter(
        (threshold) => threshold.rank < lowestRankFromHigherLevels
      );
    }

    const sortedThresholds = [...availableThresholds].sort((a, b) => {
      return a.rank - b.rank;
    });

    const options = sortedThresholds.map((threshold) => ({
      value: threshold.id,
      text: threshold.name,
    }));

    return options;
  };

  const expandedItems = createExpandedItems();
  const tableHeight = expandedItems.length > 8 ? "500px" : "auto";

  const rowActions = {
    parentButtons: [
      {
        name: "View Alert Type",
        icon: "eye",
        onClick: (row: any) => {
          setSelectedItem(row);
        },
      },
      {
        name: "Add Level",
        icon: "plus",
        onClick: (row: AlertType) => {
          const sameAlertTypeItems = selectedAlerts.filter(
            (alert) => alert.id === row.id && alert.subtypeId === row.subtypeId
          );

          if (sameAlertTypeItems.length >= 3) {
            return;
          }

          const maxLevel = Math.max(...sameAlertTypeItems.map((alert) => alert.alertLevel || 1));
          const newLevel = maxLevel + 1;

          const matchingAvailableAlert = availableAlertTypes.find(
            (available) =>
              available.id === row.id &&
              (available.subtypeId === row.subtypeId || (!available.subtypeId && !row.subtypeId))
          );

          let newThresholdId = row.thresholdId;

          if (matchingAvailableAlert?.thresholds) {
            let highestUsedRank = 0;
            sameAlertTypeItems.forEach((item) => {
              const itemThreshold = matchingAvailableAlert.thresholds?.find(
                (th) => th.id === item.thresholdId
              );
              if (itemThreshold && itemThreshold.rank > highestUsedRank) {
                highestUsedRank = itemThreshold.rank;
              }
            });

            const nextThreshold = matchingAvailableAlert.thresholds
              .filter((th) => th.rank > highestUsedRank)
              .sort((a, b) => a.rank - b.rank)[0];

            if (nextThreshold) {
              newThresholdId = nextThreshold.id;
            }
          }

          const newAlert: AlertType = {
            ...row,
            alertLevel: newLevel,
            thresholdId: newThresholdId,
            tableChecked: undefined,
            selectedObjectNames: [...(row.selectedObjectNames || [])],
          };

          const updatedAlerts = [...selectedAlerts, newAlert];
          setSelectedAlerts(updatedAlerts);
          setIsDirty(true);
        },
        isDisabled: (row: AlertType) => {
          const sameAlertTypeItems = selectedAlerts.filter(
            (alert) => alert.id === row.id && alert.subtypeId === row.subtypeId
          );

          if (sameAlertTypeItems.length >= 3) {
            return true;
          }

          const matchingAvailableAlert = availableAlertTypes.find(
            (available) =>
              available.id === row.id &&
              (available.subtypeId === row.subtypeId || (!available.subtypeId && !row.subtypeId))
          );

          // If no formula is defined
          if (!matchingAvailableAlert || !matchingAvailableAlert.formula) {
            return true;
          }

          // If no thresholds available (tolerance would show "Not Applicable")
          if (
            !matchingAvailableAlert?.thresholds ||
            matchingAvailableAlert.thresholds.length === 0
          ) {
            return true;
          }

          // Check if highest level already uses the highest available tolerance rank
          if (matchingAvailableAlert.thresholds.length > 0) {
            const maxLevel = Math.max(...sameAlertTypeItems.map((alert) => alert.alertLevel || 1));
            const highestLevelAlert = sameAlertTypeItems.find(
              (alert) => (alert.alertLevel || 1) === maxLevel
            );

            if (highestLevelAlert?.thresholdId) {
              const highestAvailableRank = Math.max(
                ...matchingAvailableAlert.thresholds.map((th) => th.rank)
              );

              const currentThreshold = matchingAvailableAlert.thresholds.find(
                (th) => th.id === highestLevelAlert.thresholdId
              );

              if (currentThreshold && currentThreshold.rank === highestAvailableRank) {
                return true;
              }
            }
          }

          return false;
        },
      },
      {
        name: "Delete Alert",
        icon: "trash",
        onClick: (row: AlertType) => {
          const updatedAlerts = selectedAlerts.filter(
            (alert) => !(alert.id === row.id && alert.subtypeId === row.subtypeId)
          );
          setSelectedAlerts(updatedAlerts);
          setIsDirty(true);
        },
        buttonProps: {
          color: "danger",
        },
      },
    ],
    title: "Actions",
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isSystemProfile) return;

    const isChecked = e.target.checked;
    const updatedAlerts = selectedAlerts.map((alert) => ({
      ...alert,
      tableChecked: isChecked ? "on" : undefined,
    }));

    // @ts-ignore
    setSelectedAlerts(updatedAlerts);
    setIsDirty(true);
  };

  const AlertHeader = () => {
    if (selectedAlerts.length === 0) {
      return <span style={{ fontWeight: 600 }}>Alert Types</span>;
    } else {
      const baseLevelAlerts = selectedAlerts.filter((alert) => alert.alertLevel === 1);

      // Check if all accordions are expanded
      const groupKeys = new Set<string>();
      selectedAlerts.forEach((alert) => {
        const groupKey = `${alert.id}-${alert.subtypeId ?? "null"}`;
        groupKeys.add(groupKey);
      });

      const allExpanded = Array.from(groupKeys).every((key) => expandedRows.has(key));

      const toggleAllAccordions = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (allExpanded) {
          // Collapse all
          setExpandedRows(new Set());
        } else {
          // Expand all
          setExpandedRows(groupKeys);
        }
      };

      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <EuiButtonIcon
            size="xs"
            iconType={allExpanded ? "arrowDown" : "arrowRight"}
            onClick={toggleAllAccordions}
            aria-label={allExpanded ? "Collapse all accordions" : "Expand all accordions"}
          />
          <span style={{ fontWeight: 600 }}>Alert Types</span>{" "}
          <BadgeCount number={baseLevelAlerts.length} />
        </div>
      );
    }
  };

  const columns: Array<EuiBasicTableColumn<ExtendedAlertType>> = [
    {
      name: (
        <EuiCheckbox
          id="select-all-alerts-checkbox"
          checked={
            selectedAlerts.length > 0 &&
            selectedAlerts.every((alert) => alert.tableChecked === "on")
          }
          disabled={isSystemProfile}
          onChange={handleSelectAllChange}
          aria-label="Select all alerts"
        />
      ),
      width: "50px",
      render: (alert: ExtendedAlertType & { isChildRow?: boolean }) => {
        const isChildRow = (alert as any).isChildRow;

        if (isChildRow) {
          return <div style={{ width: "100%" }} />;
        }

        return (
          <EuiCheckbox
            id={`table_checkbox_${getItemId(alert)}`}
            checked={alert.childRows?.every((child) => child.tableChecked === "on") || false}
            disabled={isSystemProfile}
            onChange={(e) => {
              if (isSystemProfile) return;

              const updatedAlerts = selectedAlerts.map((a) => {
                const isSameGroup = a.id === alert.id && a.subtypeId === alert.subtypeId;
                if (isSameGroup) {
                  return { ...a, tableChecked: e.target.checked ? "on" : undefined };
                }
                return a;
              });
              // @ts-ignore
              setSelectedAlerts(updatedAlerts);
              setIsDirty(true);
            }}
          />
        );
      },
    },
    {
      field: "name",
      name: <AlertHeader />,
      sortable: true,
      truncateText: false,
      render: (name: string, alert: ExtendedAlertType & { isChildRow?: boolean }) => {
        const isChildRow = (alert as any).isChildRow;

        if (isChildRow) {
          return <div style={{ width: "100%" }} />;
        }

        // For parent rows, show name with accordion toggle
        const alertId = `${alert.id}-${alert.subtypeId ?? "null"}`;
        const isExpanded = expandedRows.has(alertId);
        // Only show accordion if there are actually child rows (levels > 1)
        const hasChildren =
          alert.childRows &&
          alert.childRows.filter((child) => (child.alertLevel || 1) > 1).length > 0;

        const totalLevels = alert.childRows ? alert.childRows.length : 1;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {hasChildren && (
              <EuiButtonIcon
                size="xs"
                iconType={isExpanded ? "arrowDown" : "arrowRight"}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRowExpansion(alertId);
                }}
                aria-label="Toggle row expansion"
              />
            )}
            <span>{name}</span>
            {totalLevels > 1 && <BadgeCount number={totalLevels} />}
          </div>
        );
      },
    },
    {
      field: "alertLevel",
      name: "Level",
      sortable: true,
      width: "100px",
      align: "center",
      render: (level: number, alert: ExtendedAlertType & { isChildRow?: boolean }) => {
        const isChildRow = (alert as any).isChildRow;

        if (!isChildRow) {
          // For parent rows, just show "1" since we're showing the base level
          return (
            <Badge
              text="1"
              style={{
                color: "var(--cyan)",
                border: "1px solid var(--cyan)",
                backgroundColor: "transparent",
              }}
            />
          );
        }

        return (
          <Badge
            text={level?.toString()}
            style={{
              color: "var(--cyan)",
              border: "1px solid var(--cyan)",
              backgroundColor: "transparent",
            }}
          />
        );
      },
    },
    {
      field: "severity",
      name: "Severity",
      width: "150px",
      render: (_, alert: ExtendedAlertType & { isChildRow?: boolean }) => {
        const isChildRow = (alert as any).isChildRow;

        // Show severity for both parent (level 1) and child rows
        return (
          <EuiSelect
            key={`select-${alert.id}-${alert.severityTypeId}`}
            options={[
              { value: 1, text: "Informational" },
              { value: 3, text: "Warning" },
              { value: 5, text: "Critical" },
            ]}
            value={Number(alert.severityTypeId)}
            disabled={isSystemProfile}
            onChange={(e) => {
              handleChangeSeverity(e, alert.id, alert.subtypeId ?? null, alert.alertLevel || 1);
              setIsDirty(true);
            }}
          />
        );
      },
      sortable: false,
    },
    {
      field: "tolerance",
      name: "Tolerance",
      width: "150px",
      render: (_, alert: ExtendedAlertType & { isChildRow?: boolean }) => {
        const isChildRow = (alert as any).isChildRow;

        // Show tolerance for both parent (level 1) and child rows
        const toleranceOptions = getToleranceOptions(alert);

        return toleranceOptions.length ? (
          <EuiSelect
            key={`select-${alert.id}-${alert.thresholdId}`}
            options={toleranceOptions}
            value={Number(alert.thresholdId)}
            disabled={isSystemProfile}
            onChange={(e) => {
              handleChangeTolerance(e, alert.id, alert.subtypeId ?? null, alert.alertLevel || 1);
              setIsDirty(true);
            }}
          />
        ) : (
          "Not Applicable"
        );
      },
      sortable: false,
    },
    {
      field: "object",
      name: "Object",
      sortable: false,
      width: "250px",
      render: (_, alert: ExtendedAlertType & { isChildRow?: boolean }) => {
        const isChildRow = (alert as any).isChildRow;

        // Show object selector for both parent (level 1) and child rows
        const matchingAvailableAlert = availableAlertTypes.find(
          (available) =>
            available.id === alert.id &&
            (available.subtypeId === alert.subtypeId || (!available.subtypeId && !alert.subtypeId))
        );

        const handleObjectSelectionChange = (
          selectedObjectNames: string[],
          alertId: number,
          subtypeId: number | null,
          alertLevel: number
        ) => {
          const updatedAlerts = selectedAlerts.map((a) => {
            if (
              a.id === alertId &&
              a.subtypeId === subtypeId &&
              (a.alertLevel || 1) === alertLevel
            ) {
              return { ...a, selectedObjectNames };
            }
            return a;
          });
          setSelectedAlerts(updatedAlerts);
          setIsDirty(true);
        };

        return (
          <AlertObjectSelector
            alert={alert}
            clientId={clientId}
            monitoringProfileId={monitoringProfileId}
            onSelectionChange={handleObjectSelectionChange}
            objectTypeName={matchingAvailableAlert?.objectName}
          />
        );
      },
    },
    {
      field: "formula",
      name: (
        <>
          Tolerance
          <br />
          Formula
        </>
      ),
      sortable: false,
      render: (formula: string, alert: ExtendedAlertType & { isChildRow?: boolean }) => {
        const isChildRow = (alert as any).isChildRow;

        // Show formula for both parent (level 1) and child rows
        if (!availableAlertTypes.length) {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <EuiLoadingSpinner size="m" />
            </div>
          );
        }
        const processedFormula = processFormula(alert) || "No Formula Defined";
        return (
          <IconPopover
            icon="controlsHorizontal"
            items={[{ value: processedFormula }]}
            showCount={false}
            textSize="14px"
            copy={true}
          />
        );
      },
      width: "100px",
    },
    {
      name: "",
      align: "right",
      width: "40px",
      render: (alert: ExtendedAlertType & { isChildRow?: boolean }) => {
        const isChildRow = (alert as any).isChildRow;

        if (isChildRow) {
          const sameGroupAlerts = selectedAlerts.filter(
            (a) => a.id === alert.id && a.subtypeId === alert.subtypeId
          );

          const isDisabled = () => {
            const maxLevel = Math.max(...sameGroupAlerts.map((a) => a.alertLevel || 1));
            return (alert.alertLevel || 1) !== maxLevel;
          };

          return (
            <IconButton
              type="trash"
              onClick={() => {
                const updatedAlerts = selectedAlerts.filter(
                  (a) => getItemId(a) !== getItemId(alert)
                );
                setSelectedAlerts(updatedAlerts);
                setIsDirty(true);
              }}
              disabled={isDisabled()}
              color="#d63384"
            />
          );
        }

        return (
          <div style={{ marginRight: "4px" }}>
            <FdDataTableRowActions
              // @ts-ignore
              Buttons={rowActions.parentButtons}
              Row={alert}
              Title={rowActions.title}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div
        className={classes.tableWrapper}
        style={{
          maxHeight: tableHeight,
          overflow: expandedItems.length > 8 ? "auto" : "visible",
        }}>
        <EuiBasicTable
          key={tableKey.current}
          items={expandedItems}
          itemId={(item) => {
            if ((item as any).isChildRow) {
              return `child-${getItemId(item)}`;
            }
            return getItemId(item);
          }}
          columns={columns}
          rowHeader="name"
          className={classes.table}
          noItemsMessage="No alerts assigned"
          sorting={{
            sort: {
              field: sortField,
              direction: sortDirection,
            },
          }}
          onChange={({ sort }) => {
            if (sort) {
              setSortField(sort.field as keyof AlertType);
              setSortDirection(sort.direction);
            }
          }}
          rowProps={(item) => {
            const isChildRow = (item as any).isChildRow;

            return {
              style: {
                backgroundColor: isChildRow ? "var(--hover-background)" : undefined,
              },
            };
          }}
        />
      </div>
      {!isNullOrUndefined(selectedItem) && (
        <AlertTypeDetailsFlyout onClose={onClose} selectedItem={selectedItem} canEdit={false} />
      )}
    </>
  );
};

export default ProfileAlertsListAdvanced;
